<template>
  <LoaderWrapper ref="settingsLoader" class="h-100">
    <div class="row m-4 mt-0">
      <div class="col-sm-8 mx-auto">
        <div class="input-group input-group-sm mb-3">
          <input
              v-model="search"
              type="search"
              class="form-control"
              :placeholder="helper.trans('system_settings.search_placeholder')"
          />
          <button type="button" class="btn btn-outline-secondary text-nowrap" @click="toggleAllCategories">
            {{ allCategoriesOpen ? helper.trans('system_settings.collapse_all') : helper.trans('system_settings.expand_all') }}
          </button>
        </div>

        <div v-if="groups.length > 0 && visibleGroups.length === 0" class="text-muted">
          {{ helper.trans('system_settings.nothing_found') }}
        </div>

        <CollapsibleCard
            v-for="group in visibleGroups"
            :key="group.category"
            :title="getGroupTitle(group)"
            :count="group.items.length"
            :open="isCategoryOpen(group.category)"
            @toggle="toggleCategory(group.category)"
        >
          <div v-for="setting in group.items" :key="getSystemSettingId(setting)" class="mb-3 row">
            <label :for="'setting-' + getSystemSettingId(setting)" class="col-sm-4 col-form-label text-capitalize-first">
              {{ getSettingTitle(setting) }}
              <span class="d-block text-muted small font-monospace">{{ getSystemSettingId(setting) }}</span>
            </label>
            <div class="col-sm-5">
              <div v-if="isBooleanSystemSetting(setting)" class="form-check form-switch">
                <input
                    :id="'setting-' + getSystemSettingId(setting)"
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    :checked="isSettingChecked(setting)"
                    :disabled="isSettingDisabled(setting)"
                    @change="onBooleanChange(setting, $event)"
                />
              </div>
              <Multiselect
                  v-else-if="isMultiselectSystemSetting(setting)"
                  :id="'setting-' + getSystemSettingId(setting)"
                  :model-value="getListSettingValue(setting)"
                  @update:model-value="(value) => setListSettingValue(setting, value)"
                  :options="getSettingOptions(setting)"
                  :custom-label="(opt) => getOptionLabel(setting, opt)"
                  :multiple="true"
                  :searchable="true"
                  :taggable="isTaggableSetting(setting)"
                  @tag="(value) => addSettingTag(setting, value)"
                  :disabled="setting.readOnly === true"
                  select-label=""
                  deselect-label=""
                  :tag-placeholder="helper.trans('system_settings.add_value')"
                  :placeholder="getSettingTitle(setting)"
              >
                <template #option="props">
                  <div class="d-flex flex-row align-items-center gap-2">
                    <PayMethodIcon
                        v-if="isPayMethodOptionsSetting(setting)"
                        icon-class="icon"
                        :method="props.option"
                    ></PayMethodIcon>
                    <span class="overflow-hidden" style="white-space: nowrap; text-overflow: ellipsis">
                      {{ getOptionLabel(setting, props.option) }}
                    </span>
                  </div>
                </template>
              </Multiselect>
              <textarea
                  v-else-if="isJsonSystemSetting(setting)"
                  v-model="setting.value"
                  :id="'setting-' + getSystemSettingId(setting)"
                  rows="4"
                  class="form-control form-control-sm font-monospace"
                  :readonly="setting.readOnly === true"
              ></textarea>
              <input
                  v-else
                  v-model="setting.value"
                  :type="useNumberInput(setting) ? 'number' : 'text'"
                  :id="'setting-' + getSystemSettingId(setting)"
                  class="form-control form-control-sm"
                  :readonly="setting.readOnly === true"
              />
              <small v-if="getSettingHint(setting)" class="form-text text-muted d-block">
                {{ getSettingHint(setting) }}
              </small>
              <small v-if="setting.readOnly === true" class="form-text text-muted d-block">
                {{ helper.trans('system_settings.read_only_hint') }}
              </small>
            </div>
            <div class="col-sm-3 text-end">
              <button
                  type="button"
                  @click.prevent="saveSettings(setting)"
                  class="btn btn-sm btn-primary text-capitalize-first"
                  :disabled="isSaveDisabled(setting)"
              >
                {{ helper.trans("labels.save_changes") }}
              </button>
            </div>
          </div>
        </CollapsibleCard>
      </div>
    </div>
  </LoaderWrapper>
</template>
<script lang="ts">
import {computed, defineComponent, onMounted, reactive, ref, toRefs} from 'vue'
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import SystemSettingsUpdateRequestDTO from "@/models/request/SystemSettingsUpdateRequestDTO";
import SystemSettingsDTO, {SystemSettingsCollection, SystemSettingsGroup} from "@/models/SystemSettingsDTO";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import Multiselect from "vue-multiselect";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import CollapsibleCard from "@/components/CollapsibleCard.vue";
import {
  getSystemSettingById,
  getSystemSettingId,
  getSystemSettingsGroups,
  getSystemSettingsItems,
  getSettingOptionsSource,
  getStoreOptionsFilter,
  isBooleanSystemSetting,
  isJsonSystemSetting,
  isMultiselectSystemSetting,
  isPayMethodOptionsSetting,
  isPaymentPageSystemSetting,
  readStoredStringList,
  storeStringList,
  useNumberInput,
  isSystemSettingEnabled,
  isSettingChecked,
  getBooleanSettingValue,
  normalizeSystemSettingValue,
  parseListSettingValue,
  UNCATEGORIZED,
} from "@/service/SystemSettingsHelper";

const api = new APIClient();
const notification = useNotification()

const OPEN_CATEGORIES_STORAGE_KEY = 'systemSettingsOpenCategories';

const SETTING_HINTS: Record<string, [string, string]> = {
  provider_rating_enabled: ['labels.provider_rating_enabled_hint_off', 'labels.provider_rating_enabled_hint_on'],
  provider_rating_apply: ['labels.provider_rating_apply_hint_off', 'labels.provider_rating_apply_hint_on'],
};

const RELOAD_APP_DATA_SETTINGS = [
  'ENABLE_DISPUTE_BUTTON_ON_ACTIVE_DEALS',
  'TURNOVER_SUMMARY_ENABLED',
  'MANUAL_CURRENCY_RATES_ENABLED',
  'DEVICE_SUM_CHECK_DISABLING_ENABLED',
  'TRADER_CONCURRENT_DEALS_LIMIT_ENABLED',
  'REQUISITES_MANUAL_REORDER_ENABLED',
  'STORE_USER_WHITELIST_ENABLED',
  'STORE_COUNTERPARTY_ALLOWLIST_ENABLED',
  'STATISTIC_SUMMARY_STORE_RATE_COLUMN_ENABLED',
  'DEALS_STORE_RATE_ENABLED',
  'PAY_IN_MULTIPLES_OF_100_ENABLED',
  'OPERATOR_PAYMENT_DUTY_SEPARATION_ENABLED',
  'CHEQUE_WALES_CHECKER_ENABLED',
  'TELEGRAM_NOTIFICATION_CENTER_ENABLED',
  'TELEGRAM_BROADCAST_CENTER_ENABLED',
  'MERCHANT_DEAL_COMPLETED_TG_NOTIFY_ENABLED',
  'TRADER_RATES_BULK_EDIT_ENABLED',
  'af_stopscam_payout_receipt_enabled',
  'provider_rating_enabled',
  'provider_rating_apply',
];

export default defineComponent({
  name: 'SystemSettingsGeneral',
  components: {
    LoaderWrapper,
    Multiselect,
    PayMethodIcon,
    CollapsibleCard,
  },
  setup() {
    const store = useStore()
    const state = reactive({
      settings: [] as SystemSettingsCollection,
      search: '',
      openCategories: readStoredStringList(OPEN_CATEGORIES_STORAGE_KEY) ?? [],
      initialValues: {} as Record<string, string>,
    });

    const settingsLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const groups = computed<SystemSettingsGroup[]>(() => getSystemSettingsGroups(
        getSystemSettingsItems(state.settings)
            .filter((setting) => !isPaymentPageSystemSetting(setting))
    ));

    const matchesSearch = (setting: SystemSettingsDTO): boolean => {
      const query = state.search.trim().toLowerCase();
      if (query === '') {
        return true;
      }

      return getSettingTitle(setting).toLowerCase().includes(query)
          || getSystemSettingId(setting).toLowerCase().includes(query);
    };

    const visibleGroups = computed<SystemSettingsGroup[]>(() => groups.value
        .map((group) => ({...group, items: group.items.filter(matchesSearch)}))
        .filter((group) => group.items.length > 0));

    const allCategoriesOpen = computed(() => groups.value.length > 0
        && groups.value.every((group) => state.openCategories.includes(group.category)));

    const isCategoryOpen = (category: string): boolean =>
        state.search.trim() !== '' || state.openCategories.includes(category);

    const toggleCategory = (category: string) => {
      if (state.openCategories.includes(category)) {
        state.openCategories = state.openCategories.filter((item) => item !== category);
      } else {
        state.openCategories = [...state.openCategories, category];
      }

      storeStringList(OPEN_CATEGORIES_STORAGE_KEY, state.openCategories);
    };

    const toggleAllCategories = () => {
      state.openCategories = allCategoriesOpen.value ? [] : groups.value.map((group) => group.category);
      storeStringList(OPEN_CATEGORIES_STORAGE_KEY, state.openCategories);
    };

    const isApplyDisabled = () => !isSystemSettingEnabled(state.settings, 'provider_rating_enabled')

    const isSettingDisabled = (setting: SystemSettingsDTO): boolean =>
        setting.readOnly === true
        || (getSystemSettingId(setting) === 'provider_rating_apply' && isApplyDisabled());

    const isSaveDisabled = (setting: SystemSettingsDTO): boolean => {
      if (setting.readOnly === true) {
        return true;
      }

      if (!isMultiselectSystemSetting(setting)) {
        return false;
      }

      const wasEmpty = (state.initialValues[getSystemSettingId(setting)] ?? '') === '';

      return wasEmpty && getListSettingValue(setting).length === 0;
    };

    const fetchSettings = async () => {
      try {
        settingsLoader.value?.show();
        state.settings = await api.systemSettings();
        state.initialValues = {};
        getSystemSettingsItems(state.settings).forEach((setting) => {
          state.initialValues[getSystemSettingId(setting)] = setting.value ?? '';
        });

        if (state.openCategories.length === 0 && groups.value.length > 0) {
          state.openCategories = [groups.value[0].category];
        }
      } catch (error) {
        notification.notify({
          title: helper.trans('system_settings.fetch_failed'),
          type: 'error',
        });
      } finally {
        settingsLoader.value?.hide();
      }
    };

    const getSettingTitle = (setting: SystemSettingsDTO) => {
      const id = getSystemSettingId(setting);
      if (!id) {
        return setting.title ?? '';
      }
      const key = `system_settings.${id}`;
      const translated = helper.trans(key);
      return translated !== key ? translated : (setting.title ?? '');
    };

    const getGroupTitle = (group: SystemSettingsGroup): string => {
      if (group.title) {
        return group.title;
      }

      const key = `system_settings.category_${group.category}`;
      const translated = helper.trans(key);
      return translated !== key ? translated : helper.trans(`system_settings.category_${UNCATEGORIZED}`);
    };

    const getSettingHint = (setting: SystemSettingsDTO) => {
      const keys = SETTING_HINTS[getSystemSettingId(setting)]
      return keys ? helper.trans(keys[isSettingChecked(setting) ? 1 : 0]) : ''
    }

    const getSettingOptions = (setting: SystemSettingsDTO): string[] => {
      const storeFilter = getStoreOptionsFilter(setting);
      if (storeFilter !== null) {
        return store.getters['currencyMethods/methods'](storeFilter[0], storeFilter[1]);
      }

      return setting.options ?? [];
    };

    const isTaggableSetting = (setting: SystemSettingsDTO): boolean =>
        getSettingOptionsSource(setting) === null
        && (setting.options ?? []).length === 0;

    const getOptionLabel = (setting: SystemSettingsDTO, option: string): string =>
        isPayMethodOptionsSetting(setting) ? helper.trans(`pay_methods.${option}`, {}, option) : option;

    const getListSettingValue = (setting: SystemSettingsDTO): string[] =>
        parseListSettingValue(setting.value);

    const setListSettingValue = (setting: SystemSettingsDTO, value: string[]) => {
      setting.value = normalizeSystemSettingValue(setting, value).value;
    };

    const addSettingTag = (setting: SystemSettingsDTO, value: string) => {
      const list = getListSettingValue(setting);
      if (list.includes(value)) {
        return;
      }

      setListSettingValue(setting, [...list, value]);
    };

    const onBooleanChange = (setting: SystemSettingsDTO, event: Event) => {
      const checked = (event.target as HTMLInputElement).checked;
      const id = getSystemSettingId(setting);
      setting.value = getBooleanSettingValue(id, checked);

      if (id === 'provider_rating_enabled' && setting.value === '0') {
        const applySetting = getSystemSettingById(state.settings, 'provider_rating_apply')
        if (applySetting) applySetting.value = '0'
      }
    };

    const saveSettings = async (setting: SystemSettingsDTO) => {
      const id = getSystemSettingId(setting);

      try {
        settingsLoader.value?.show();

        const requestDTO: SystemSettingsUpdateRequestDTO = normalizeSystemSettingValue(setting, setting.value);

        await api.updateSystemSettings(id, requestDTO);

        if (id === 'provider_rating_enabled' && requestDTO.value === '0') {
          await api.updateSystemSettings('provider_rating_apply', {value: '0'})
        }

        notification.notify({
          title: helper.trans('system_settings.save_success'),
          type: 'success',
        });

        if (RELOAD_APP_DATA_SETTINGS.includes(id)) {
          await store.dispatch('loadAppData');
        }

        await fetchSettings();
      } catch (error) {
        notification.notify({
          title: helper.trans('system_settings.save_failed'),
          type: 'error',
        });
      } finally {
        settingsLoader.value?.hide();
      }
    };

    onMounted(() => {
      fetchSettings();
      store.dispatch('currencyMethods/loadList');
    });

    return {
      ...toRefs(state),
      groups,
      visibleGroups,
      allCategoriesOpen,
      isCategoryOpen,
      toggleCategory,
      toggleAllCategories,
      fetchSettings,
      saveSettings,
      onBooleanChange,
      getSettingHint,
      getSettingTitle,
      getGroupTitle,
      getSystemSettingId,
      isBooleanSystemSetting,
      isMultiselectSystemSetting,
      isJsonSystemSetting,
      isPayMethodOptionsSetting,
      useNumberInput,
      isSettingDisabled,
      isSaveDisabled,
      isTaggableSetting,
      getSettingOptions,
      getOptionLabel,
      getListSettingValue,
      setListSettingValue,
      addSettingTag,
      isSettingChecked,
      helper,
      settingsLoader,
    };
  },
})
</script>
